import { isSuccess } from '@/context/services/type';
import { parseFileName } from '@/context/services/utils/contentDisposition';
import { extractCommonErrorFromBlob } from '@/context/services/utils/extractCommonErrorFromBlob';
import { useTranslation } from '@/i18n';
import { isCommonError } from '@/libs/typeguard/isError';
import { useQueryClient } from '@tanstack/react-query';
import type { CsvEncodeParameter } from 'ap-openapi';
import {
  getGetReceivedInvoiceQueryKey,
  RequestReceivedInvoiceTransactionOcrMutationError,
  useGetReceivedInvoiceTransactionOcrCsvHook,
  useRequestReceivedInvoiceTransactionOcr,
} from 'ap-openapi';
import { AxiosResponse, isAxiosError } from 'axios';
import { useCallback } from 'react';

type TRequestReceivedInvoiceTransactionOcrMutateAsync = ReturnType<
  typeof useRequestReceivedInvoiceTransactionOcr
>['mutateAsync'];
type RequestReceivedInvoiceTransactionOcrMutateArgs =
  Parameters<TRequestReceivedInvoiceTransactionOcrMutateAsync>['0'];

export const useReceivedInvoiceTransactionOcr = () => {
  const { t } = useTranslation();
  const query = useQueryClient();
  const { mutateAsync: _mutateAsync } = useRequestReceivedInvoiceTransactionOcr(
    {
      mutation: {
        onSuccess: async (_data, { id }) => {
          await Promise.all([
            query.invalidateQueries({
              queryKey: getGetReceivedInvoiceQueryKey(id),
            }),
          ]);
        },
      },
    }
  );
  const mutateAsync = useCallback(
    async (variable: RequestReceivedInvoiceTransactionOcrMutateArgs) => {
      let resp: AxiosResponse<void | RequestReceivedInvoiceTransactionOcrMutationError>;
      try {
        resp = await _mutateAsync(variable);
      } catch (error) {
        throw error instanceof Error ? error : new Error(String(error));
      }
      if (isSuccess(resp.status)) return { isSuccessful: true } as const;

      if (!resp.data || !isCommonError(resp.data)) {
        throw new Error(
          `Unexpected transaction OCR response shape: ${resp.status}`
        );
      }

      const { code, messages } = resp.data;

      return {
        isSuccessful: false,
        code: code ?? '',
        message: messages?.length
          ? messages.join('\n')
          : t('received_invoice_transaction_request_error'),
      } as const;
    },
    [_mutateAsync, t]
  );
  return { mutateAsync };
};

type ReceivedInvoiceTransactionOcrCsvArgs = {
  id: string;
  csvEncode: CsvEncodeParameter;
};

export const useReceivedInvoiceTransactionOcrCsv = () => {
  const { t } = useTranslation();
  const getCsv = useGetReceivedInvoiceTransactionOcrCsvHook();

  return useCallback(
    async ({ id, csvEncode }: ReceivedInvoiceTransactionOcrCsvArgs) => {
      let response: AxiosResponse<Blob>;
      try {
        response = await getCsv(id, { csv_encode: csvEncode });
      } catch (error) {
        if (!isAxiosError<Blob>(error) || !error.response) {
          throw error instanceof Error ? error : new Error(String(error));
        }
        response = error.response;
      }

      if (isSuccess(response.status)) {
        return {
          isSuccessful: true,
          blob: response.data,
          fileName: parseFileName(response.headers, 'transaction_ocr.csv'),
        } as const;
      }

      const commonError = await extractCommonErrorFromBlob(response.data);
      if (!commonError) {
        throw new Error(
          `Unexpected transaction OCR CSV response shape: ${response.status}`
        );
      }

      const { code, messages } = commonError;

      return {
        isSuccessful: false,
        code: code ?? '',
        message: messages?.length
          ? messages.join('\n')
          : t('received_invoice_transaction_export_error'),
      } as const;
    },
    [getCsv, t]
  );
};
